import { DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Row,
  Select,
  Spin,
  Table,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "../../../components/layouts/MainLayout";
import TopContent from "../../../components/layouts/TopContent";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../teller.config";
const { Text, Title } = Typography;

const ReceiveTellerTransaction = (props) => {
  const { userData } = props;
  const { session } = userData;
  const { state } = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [transactionDetail, setTransactionDetail] = useState([]);
  const [denominationForm] = Form.useForm();
  const [denominationEntries, setDenominationEntries] = useState([]);
  const [selectedDenominationValue, setSelectedDenominationValue] = useState(0);
  const [denominationCount, setDenominationCount] = useState(0);
  const [currencyDenominationOptions, setCurrencyDenominationOptions] =
    useState([]);

  useEffect(() => {
    // if (!state?.id) {
    //   return;
    // }
    getDetailById(state?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.id]);

  const getDetailById = (id) => {
    const url = `${apiConfig.Transaction}/${id}`;
    const body = { session };

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        setIsLoading(false);
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to fetch transaction detail",
        );
      } else {
        const objList = res;
        let tempDenominationOptions = [];

        objList?.denomCodes?.forEach((element) => {
          tempDenominationOptions.push({
            label: element,
            value: element,
          });
        });

        setTransactionDetail(objList);
        setCurrencyDenominationOptions(tempDenominationOptions);
        setIsLoading(false);
      }
    });
  };

  const detailValues = useMemo(
    () => ({
      transactionDate: BOM.DateTime(transactionDetail?.transactionDate) || "-",
      description:
        transactionDetail?.txnType === "REQ"
          ? "Request Cash From Vault"
          : transactionDetail?.txnType === "SND"
            ? "Send Cash to Vault"
            : transactionDetail?.txnType === "WTH"
              ? "Teller Cash Withdrawal"
              : transactionDetail?.txnType === "DEP"
                ? "Teller Cash Deposit"
                : transactionDetail?.txnType === "CLO"
                  ? "Teller Batch Closure"
                  : "-",
      tellerName:
        transactionDetail?.teller?.appUser?.firstname +
        " " +
        transactionDetail?.teller?.appUser?.lastname,
      amount: transactionDetail?.amount?.toLocaleString() || "-",
      currencyCode:
        transactionDetail?.currencyCode ||
        transactionDetail?.transactionCurrencyCode ||
        "-",
      transactionStatus:
        transactionDetail?.status === 2 ? "Received" : "Unprocessed",
    }),
    [transactionDetail],
  );

  const dataSource = [
    {
      key: "1",
      description: "Transaction Date",
      item: detailValues.transactionDate,
    },
    { key: "2", description: "Description", item: detailValues.description },
    { key: "3", description: "Teller Name", item: detailValues.tellerName },
    {
      key: "4",
      description: "Amount",
      item: BOM.FormatAmount(detailValues.amount),
    },
    { key: "5", description: "Currency Code", item: detailValues.currencyCode },
    {
      key: "6",
      description: "Transaction Status",
      item: detailValues.transactionStatus,
    },
  ];

  const columns = [
    {
      dataIndex: "description",
      key: "description",
      width: "40%",
      onCell: () => ({
        style: {
          backgroundColor: "#F5F5F5",
        },
      }),
      render: (text) => (
        <Text strong style={{ cdolor: "#8C8C8C" }}>
          {text}
        </Text>
      ),
    },
    {
      dataIndex: "item",
      key: "item",
      width: "60%",
    },
  ];

  const denominationColumns = [
    { title: "Currency", dataIndex: "currency", key: "currency" },
    {
      title: "Currency Denomination",
      dataIndex: "currencyDenomination",
      key: "currencyDenomination",
    },
    {
      title: "Denomination Count",
      dataIndex: "denominationCount",
      key: "denominationCount",
    },
    {
      title: "Denomination Value",
      dataIndex: "denominationValue",
      key: "denominationValue",
      render: (value) => BOM.FormatAmount(value),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          className="of-primaryBtnColor"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveDenomination(record)}
        />
      ),
    },
  ];

  const handleRemoveDenomination = (record) => {
    setDenominationEntries(
      denominationEntries.filter((e) => e.key !== record.key),
    );
  };

  const handleDenominationChange = (value) => {
    setSelectedDenominationValue(Number(value || 0));
    denominationForm.setFieldsValue({
      denominationValue: Number(value || 0) * Number(denominationCount || 0),
    });
  };

  const handleCountChange = (value) => {
    const countValue = Number(value || 0);
    setDenominationCount(countValue);
    denominationForm.setFieldsValue({
      denominationValue: Number(selectedDenominationValue || 0) * countValue,
    });
  };

  const handleAddDenomination = () => {
    denominationForm
      .validateFields(["currencyDenomination", "denominationCount"])
      .then((values) => {
        const denomAmount =
          Number(String(values.currencyDenomination ?? "").replace(/,/g, "")) *
          Number(values.denominationCount ?? 0);

        if (!Number.isFinite(denomAmount) || denomAmount < 0) {
          BOM.AlertMsg(
            cnt.WARNING,
            "Please select a valid denomination and count",
          );
          return;
        }

        setDenominationEntries((prev) => [
          ...prev,
          {
            key: `${Date.now()}-${prev.length + 1}`,
            currency: detailValues.currencyCode,
            currencyDenomination: values.currencyDenomination,
            denominationCount: values.denominationCount,
            denominationValue: denomAmount,
          },
        ]);

        denominationForm.setFieldsValue({
          currencyDenomination: undefined,
          denominationCount: undefined,
          denominationValue: undefined,
        });
        setSelectedDenominationValue(0);
        setDenominationCount(0);
      });
  };

  const getVaultCurrencyInfos = () =>
    denominationEntries.map((entry) => ({
      vaultCurrencyInfo: {
        locale: "en",
        currencyCode: transactionDetail?.currencyCode,
        denomCode: entry.currencyDenomination,
        denomCount: String(entry.denominationCount),
        denomValue: entry.denominationValue,
        denomName: transactionDetail?.currencyCode,
      },
    }));

  const onSubmit = (values) => {
    setIsLoading(true);
    const payload = {
      // ...values,
      action: "REC",
      // allowDenomTracking: true,
      amount: transactionDetail?.amount,
      currencyCode: transactionDetail?.currencyCode,
      id: transactionDetail.id,
      txnType: transactionDetail?.txnType,
      transactionDate: BOM.DateTime(
        transactionDetail?.transactionDate,
        "only-date",
      ),
      isClientTrx: false,
      office: userData.officeId,
      status: "2",
      tillBatchId: transactionDetail?.tillBatchId,
      txnNote: transactionDetail?.txnNote,
      method: "PUT",
      session,
      locale: "en",
      vaultCurrencyInfos: getVaultCurrencyInfos(),
      dateFormat: "dd MMMM yyyy",
    };

    const url = `${apiConfig.Transaction}?id=${state?.id}`;

    BOM.FetchReqAction(payload, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setIsLoading(false);
      } else {
        BOM.AlertMsg(cnt.SUCCESS, "Sent cash successfully");
        setIsLoading(false);
        navigate("/tlo0300");
      }
    });
  };

  const hasDenominationEntries = () => {
    if (denominationEntries.length > 0) return true;

    BOM.AlertMsg(
      cnt.WARNING,
      "Please add at least one currency denomination entry",
    );
    return false;
  };

  const handleProcess = () => {
    if (!hasDenominationEntries()) return;

    onSubmit({
      ...denominationForm.getFieldsValue(),
    });
  };

  return (
    <MainLayout isEnableRightSidebar={true}>
      <TopContent
        pageTitle={labelConfig.tellerTransactionDetail}
        pageItems={staticConfig.tellerTransactionDetailItems}
      />
      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        <Spin spinning={isLoading} size="large">
          <Form
            // onFinish={onSubmit}
            form={denominationForm}
            layout="vertical"
            style={{ marginTop: 24 }}
          >
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Button
                className="of-primaryBtnColor"
                style={{
                  width: "10%",
                }}
                onClick={handleProcess}
              >
                Process
              </Button>
            </Row>
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              showHeader={false}
              bordered
            />

            <Divider style={{ marginTop: 8, marginBottom: 16 }} />

            <Title
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
              level={5}
            >
              Vault Denomination Details
            </Title>
            <Row gutter={16} align="bottom" style={{ marginBottom: 12 }}>
              <Col span={6}>
                <Form.Item
                  name="currencyDenomination"
                  label={
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Currency Denomination
                    </Text>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Currency denomination is required",
                    },
                  ]}
                >
                  <Select
                    style={{ width: "100%", height: 44 }}
                    placeholder="Select denomination"
                    options={currencyDenominationOptions}
                    onChange={handleDenominationChange}
                    showSearch
                    allowClear
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name="denominationCount"
                  label={
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Denomination count
                    </Text>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Denomination count is required",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%", height: 44, paddingTop: 7 }}
                    onChange={handleCountChange}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name="denominationValue"
                  label={
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Denomination value
                    </Text>
                  }
                >
                  <InputNumber
                    style={{ width: "100%", height: 44, paddingTop: 7 }}
                    placeholder="0"
                    className="of-bold-disable-input"
                    formatter={BOM.FormatAmount}
                    parser={BOM.ParseAmount}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label=" ">
                  <Button
                    type="primary"
                    className="of-primaryBtnColor"
                    style={{ height: 44 }}
                    onClick={handleAddDenomination}
                  >
                    Add
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            {denominationEntries.length > 0 && (
              <Table
                style={{ marginTop: 16, marginBottom: 24 }}
                dataSource={denominationEntries}
                columns={denominationColumns}
                pagination={false}
                size="small"
              />
            )}
          </Form>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReceiveTellerTransaction);
